import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import { WysiwygField, Button } from '@uncinc/uncinc-react-kitchen-sink';

import { clearPageInformation, setPageInformation, setPageViewSent } from '../../../../store/actions/UI';

import './style.scss';
import { DEFAULT_LANGUAGE } from '../../../../config/constants';
import { ROUTE_CUSTOMER_SERVICE } from '../../../../config/routes';

const Success = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const orderNumber = useSelector((state) => state.getIn(['uiInternal', 'prevOrderNumber'], ''));
  const prevOrderInfo = useSelector((state) => state.getIn(['uiInternal', 'prevOrderInfo']));

  const isElearning = prevOrderInfo?.get('isElearning') || false;
  const isFreeProduct = prevOrderInfo?.get('isFreeProduct') || false;
  const productType = prevOrderInfo?.get('productType') || '';
  const isDigitalTool = productType === 'digital_tool';

  const getPageTitle = () => {
    if (isDigitalTool) return `${t('checkout.success.digital_tool_title')} | VeiligheidNL`;
    if (isElearning && isFreeProduct) return `${t('checkout.success.elearning_free_title')} | VeiligheidNL`;
    return `${t('checkout.success.title')} | VeiligheidNL`;
  };
  const PAGE_TITLE = getPageTitle();

  useEffect(() => {
    dispatch(clearPageInformation());
    dispatch(setPageInformation(PAGE_TITLE));
    dispatch(setPageViewSent(false));
  }, []);

  /**
   * Renders the success content for e-learning products (free or paid).
   */
  const renderElearningContent = () => {
    const prefix = isFreeProduct ? 'elearning_free' : 'elearning_paid';

    return (
      <>
        <h2>{t(`checkout.success.${prefix}_title`)}</h2>
        <p>{t(`checkout.success.${prefix}_body`)}</p>
        <WysiwygField html={t(`checkout.success.${prefix}_questions`, { customerServiceUrl: ROUTE_CUSTOMER_SERVICE })} />
      </>
    );
  };

  /**
   * Renders the success content for digital tool products.
   */
  const renderDigitalToolContent = () => (
    <>
      <h2>{t('checkout.success.digital_tool_title')}</h2>
      <p>{t('checkout.success.digital_tool_body')}</p>
      <p>{t('checkout.success.digital_tool_order_number', { orderNumber })}</p>
      <WysiwygField html={t('checkout.success.digital_tool_questions', { faqUrl: '/veelgestelde-vragen' })} />
    </>
  );

  /**
   * Renders the default success content for non-e-learning products.
   */
  const renderDefaultContent = () => (
    <>
      <h2>{t('checkout.success.title')}</h2>
      <p>{t('checkout.success.email_confirm')}</p>
      <p>{t('checkout.success.order_number', { orderNumber: orderNumber })}</p>
      <WysiwygField html={t('checkout.success.questions', { faqUrl: ROUTE_CUSTOMER_SERVICE })} />
    </>
  );

  const renderContent = () => {
    if (isDigitalTool) return renderDigitalToolContent();
    if (isElearning) return renderElearningContent();
    return renderDefaultContent();
  };

  return (
    <>
      <Helmet
        htmlAttributes={{
          lang: DEFAULT_LANGUAGE,
        }}
      >
        <title>{PAGE_TITLE}</title>
      </Helmet>
      <div className="Success">
        <div className="Success__container">
          <div className="StepsProgressBar__step__number__done"><span><div className="icon icon-check icon-variant-2" /></span></div>

          {renderContent()}

          <Button onClick={() => history.push('/')} className="Button Button--primary">{t('cart.back_to_home')}</Button>
        </div>
      </div>
    </>
  );
};

export default Success;
